import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, FilePlus, Paperclip, Info, ChevronDown, ChevronRight } from "lucide-react";
import { buildRatePreview } from "@/lib/rentalPricing";
import { EquipmentSection } from "@/components/EquipmentSection";
import ReplacementValueInput from "@/components/ReplacementValueInput";

const EMPTY_FORM = {
    // Equipment subset — owned by <EquipmentSection> (Phase AL1+AE).
    equipment_type: "",
    equipment_type_other: "",
    unit_number: "",
    make: "", model: "", year: "",
    vin: "", plate: "",
    equipment_size: "",
    alvys_trailer_id: "",
    alvys_synced_at: "",
    // Import-specific fields.
    start_date: "", end_date: "",
    daily_rate: "", deposit: "",
    monthly_rate: "", minimum_term_months: "", billing_period: "",
    // Phase RV — replacement value, optional at import time. Persists
    // onto the imported rental and unlocks COI requests downstream.
    value: null,
    status_preselect: "active",
    notes: "",
    also_trigger_coi_workflow: false,
    // Phase N.8 (2026-02) — optional first-invoice seed at import
    // time. When left blank AND status_preselect="active", the server
    // auto-bypasses the J.8 first-invoice gate (audit trail records
    // ``auto_filled=true``). The admin can still expand the collapsed
    // "Optional: attach first invoice now" section to seed the first
    // invoice at import time.
    first_invoice_amount_cents: "",
    first_invoice_service_period_start: "",
    // Phase N.8 — bypass reason. Auto-filled on the FE, admin can
    // click "Edit reason" to override. Server accepts and records it
    // in the audit event.
    skip_invoice_gate_reason:
        "Rental imported at status=active — first invoice deferred",
};

const DEFAULT_BYPASS_REASON =
    "Rental imported at status=active — first invoice deferred";

export function ImportRentalDialog({ customerId, open, onOpenChange, onCreated }) {
    const navigate = useNavigate();
    const [form, setForm] = useState(EMPTY_FORM);
    const [paperContract, setPaperContract] = useState(null);
    const [existingCoi, setExistingCoi] = useState(null);
    // Phase AE follow-up — optional imported ACH authorization form.
    const [existingAch, setExistingAch] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    // Phase N.8 — collapsed by default. Admin expands to attach first
    // invoice seed data at import time (defers to post-import invoice
    // creation otherwise).
    const [showInvoiceFields, setShowInvoiceFields] = useState(false);
    // Phase N.8 — collapsed "Advanced" section for the bypass reason.
    // Auto-filled + read-only by default; admin can click "Edit reason"
    // to override before submitting.
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [reasonEditable, setReasonEditable] = useState(false);

    const reset = () => {
        setForm(EMPTY_FORM);
        setPaperContract(null);
        setExistingCoi(null);
        setExistingAch(null);
        setShowInvoiceFields(false);
        setShowAdvanced(false);
        setReasonEditable(false);
    };
    const close = () => { onOpenChange(false); reset(); };

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const validate = () => {
        if (!form.equipment_type) return "Equipment type is required";
        if (form.equipment_type === "other" && !form.equipment_type_other.trim()) return "Describe the equipment";
        if (!form.unit_number.trim()) return "Unit number is required";
        if (!form.start_date) return "Start date is required";
        if (!paperContract) return "Paper contract PDF or DOCX is required";
        // Accept PDF or DOCX. Some browsers send DOCX with an empty
        // .type — fall back to a filename-extension check so we don't
        // reject a perfectly fine .docx upload.
        const okPaperType = paperContract && (
            paperContract.type === "application/pdf"
            || paperContract.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            || /\.(pdf|docx)$/i.test(paperContract.name || "")
        );
        if (paperContract && !okPaperType) return "Paper contract must be a PDF or DOCX";
        if (existingCoi && !["application/pdf", "image/jpeg", "image/png"].includes(existingCoi.type)) return "Existing COI must be PDF, JPG, or PNG";
        if (existingAch && !["application/pdf", "image/jpeg", "image/png"].includes(existingAch.type)) return "Existing ACH form must be PDF, JPG, or PNG";
        return null;
    };

    const onSubmit = async () => {
        const err = validate();
        if (err) { toast.error(err); return; }
        setSubmitting(true);
        try {
            const fd = new FormData();
            fd.append("equipment_type", form.equipment_type);
            if (form.equipment_type === "other" && form.equipment_type_other) fd.append("equipment_type_other", form.equipment_type_other);
            fd.append("unit_number", form.unit_number);
            fd.append("start_date", form.start_date);
            if (form.end_date) fd.append("end_date", form.end_date);
            ["make", "model", "vin", "plate", "notes", "equipment_size"].forEach((k) => { if (form[k]) fd.append(k, form[k]); });
            ["year", "daily_rate", "monthly_rate", "deposit", "minimum_term_months"].forEach((k) => { if (form[k] !== "" && form[k] != null) fd.append(k, form[k]); });
            if (form.billing_period) fd.append("billing_period", form.billing_period);
            // Phase AE follow-up — Alvys provenance. EquipmentSection only
            // sets these two when the admin clicked "Use this data".
            if (form.alvys_trailer_id) fd.append("alvys_trailer_id", form.alvys_trailer_id);
            if (form.alvys_synced_at) fd.append("alvys_synced_at", form.alvys_synced_at);
            fd.append("status_preselect", form.status_preselect);
            // Phase RV — pass replacement value when set. Server rounds
            // to integer dollars and treats null/<=0 as unset.
            if (form.value != null && form.value > 0) fd.append("value", String(form.value));
            fd.append("also_trigger_coi_workflow", form.also_trigger_coi_workflow ? "true" : "false");
            fd.append("paper_contract", paperContract);
            if (existingCoi) fd.append("existing_coi", existingCoi);
            // Phase AE follow-up — optional imported ACH form.
            if (existingAch) fd.append("ach_form", existingAch);
            // Phase N.8 — either the admin seeded a first invoice
            // (both fields present) OR we defer with the auto-bypass
            // reason. Never send ``skip_invoice_gate=true`` — the
            // server auto-derives that from the presence/absence of
            // the invoice fields. We always send the reason so the
            // audit trail captures the admin's edit when they chose
            // to override the default.
            const seedAmt = String(form.first_invoice_amount_cents || "").trim();
            const seedStart = String(form.first_invoice_service_period_start || "").trim();
            if (showInvoiceFields && seedAmt && seedStart) {
                fd.append("first_invoice_amount_cents", seedAmt);
                fd.append("first_invoice_service_period_start", seedStart);
            } else if (form.status_preselect === "active") {
                // Deferred first-invoice path — carry the reason for
                // the server-side audit event.
                const reason = String(form.skip_invoice_gate_reason || "").trim()
                    || DEFAULT_BYPASS_REASON;
                fd.append("skip_invoice_gate_reason", reason);
            }

            const { data } = await api.post(`/admin/customers/${customerId}/import-rental`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            // Phase B1 follow-up — surface the auto-created schedule in
            // the success toast so the admin sees billing context inline.
            const sched = data && data.auto_created_schedule;
            if (sched && sched.amount_cents) {
                const amt = (sched.amount_cents / 100).toLocaleString("en-US", {
                    minimumFractionDigits: 0, maximumFractionDigits: 2,
                });
                const dayLabel = sched.day_of_month ? `day ${sched.day_of_month}` : sched.cadence_kind;
                toast.success(`Rental imported. Monthly billing of $${amt} starts on ${dayLabel}.`, {
                    duration: 9000,
                    action: {
                        label: "Edit schedule",
                        // Phase B1 follow-up #3 — deep-link to the rental
                        // detail page with the BillingScheduleModal opened
                        // via ``?openSchedule=<id>``.
                        onClick: () => navigate(
                            `/admin/rentals/${data.rental_id}?openSchedule=${sched.id}`,
                        ),
                    },
                });
            } else {
                toast.success("Rental imported");
            }
            close();
            if (onCreated) onCreated(data);
        } catch (e) { toast.error(formatApiError(e)); } finally { setSubmitting(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) close(); else onOpenChange(true); }}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="import-rental-dialog">
                <DialogHeader>
                    <DialogTitle>Add a current rental</DialogTitle>
                    <DialogDescription>Bring an existing paper-contract rental into RentFlow. The original contract PDF is the source of truth — no digital signing required.</DialogDescription>
                </DialogHeader>

                <div className="space-y-5">
                    {/* Phase N.8 — importing an existing rental means the first-invoice
                        gate doesn't need to fire at import time. The server auto-fills
                        the bypass; the admin can still expand "Optional: attach first
                        invoice now" below to seed one here. */}
                    <div
                        className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900 flex items-start gap-3"
                        data-testid="import-rental-callout"
                    >
                        <Info size={18} className="mt-0.5 shrink-0 text-blue-700" />
                        <div>
                            <p className="font-semibold">Importing an existing rental</p>
                            <p className="mt-1 text-blue-800">
                                This rental is already in progress. The first-invoice requirement
                                doesn't apply — you can add the first invoice later from the rental
                                detail page.
                            </p>
                        </div>
                    </div>

                    {/* Phase AE follow-up — equipment chips + Alvys lookup banner
                        now live in <EquipmentSection> so the admin gets the same
                        prefill UX as the AE / Create-Rental dialogs. */}
                    <section className="space-y-2">
                        <Label className="text-xs uppercase tracking-wider text-slate-500 block">Equipment</Label>
                        <EquipmentSection
                            value={form}
                            onChange={setField}
                            open={open}
                            testIdPrefix="import-rental-eq-"
                        />
                    </section>

                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                        <Field label="Start date" required type="date" v={form.start_date} onChange={(v) => setField("start_date", v)} testId="import-rental-start-date" />
                        <Field label="End date" type="date" v={form.end_date} onChange={(v) => setField("end_date", v)} testId="import-rental-end-date" />
                        <Field label="Daily rate ($)" type="number" v={form.daily_rate} onChange={(v) => setField("daily_rate", v)} testId="import-rental-rate" />
                        <Field label="Monthly rate ($)" type="number" v={form.monthly_rate} onChange={(v) => setField("monthly_rate", v)} testId="import-rental-monthly-rate" />
                        <div className="space-y-1.5">
                            <Label>Billing period</Label>
                            <div className="flex gap-2" data-testid="import-rental-billing-period">
                                {["daily", "monthly"].map((p) => {
                                    const checked = (form.billing_period || (form.monthly_rate && !form.daily_rate ? "monthly" : (form.daily_rate ? "daily" : ""))) === p;
                                    return (
                                        <button key={p} type="button"
                                            onClick={() => setField("billing_period", p)}
                                            data-testid={`import-rental-billing-${p}`}
                                            className={`flex-1 h-10 rounded-md border text-xs font-medium ${
                                                checked ? "bg-blue-900 border-blue-900 text-white" : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
                                            }`}>{p === "daily" ? "Daily" : "Monthly"}</button>
                                    );
                                })}
                            </div>
                        </div>
                        <Field label="Min term (months)" type="number" v={form.minimum_term_months} onChange={(v) => setField("minimum_term_months", v)} testId="import-rental-min-term" />
                        <Field label="Deposit ($)" type="number" v={form.deposit} onChange={(v) => setField("deposit", v)} testId="import-rental-deposit" />
                        <div className="space-y-1.5 col-span-2">
                            <Label>Status preselect</Label>
                            <select value={form.status_preselect} onChange={(e) => setField("status_preselect", e.target.value)}
                                className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                                data-testid="import-rental-status">
                                <option value="active">Active (currently on rent)</option>
                                <option value="scheduled">Scheduled (starts later)</option>
                                <option value="ended">Ended (already returned)</option>
                            </select>
                        </div>
                    </div>

                    {(form.daily_rate || form.monthly_rate) && (
                        <div className="rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs text-slate-700"
                            data-testid="import-rental-pricing-preview">
                            {buildRatePreview({
                                daily_rate: form.daily_rate ? Number(form.daily_rate) : null,
                                monthly_rate: form.monthly_rate ? Number(form.monthly_rate) : null,
                                minimum_term_months: form.minimum_term_months ? Number(form.minimum_term_months) : null,
                                billing_period: form.billing_period || null,
                                start_date: form.start_date,
                            })}
                        </div>
                    )}

                    {/* Phase RV — equipment replacement value. Optional at
                        import time; required at COI-request send time. */}
                    <div className="max-w-xs">
                        <ReplacementValueInput
                            value={form.value}
                            onChange={(v) => setField("value", v)}
                            testIdBase="import-rental-replacement-value"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label>Notes</Label>
                        <Textarea rows={2} value={form.notes} onChange={(e) => setField("notes", e.target.value)}
                            placeholder="Anything noteworthy about the rental — special terms, condition, etc."
                            data-testid="import-rental-notes" />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <DropZone
                            label="Paper contract (PDF or DOCX)"
                            required
                            accept="application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.pdf,.docx"
                            file={paperContract}
                            onChange={setPaperContract}
                            testId="import-rental-contract"
                            hint="The signed paper contract that's the source of truth. DOCX files are converted to PDF automatically."
                        />
                        <DropZone
                            label="Existing COI (PDF/JPG/PNG)"
                            accept="application/pdf,image/jpeg,image/png"
                            file={existingCoi}
                            onChange={setExistingCoi}
                            testId="import-rental-coi"
                            hint="Optional. If provided it'll be saved as a pre-approved COI on the rental."
                        />
                        {/* Phase AE follow-up — third optional upload card. */}
                        <DropZone
                            label="Existing ACH form (PDF/JPG/PNG)"
                            accept="application/pdf,image/jpeg,image/png"
                            file={existingAch}
                            onChange={setExistingAch}
                            testId="import-rental-ach"
                            hint="Optional. If provided, the signed ACH authorization will be attached to the rental as a pre-approved ACH form."
                        />
                    </div>

                    <label className="flex items-start gap-2 text-sm border border-slate-200 rounded-md p-3 bg-slate-50/50">
                        <input type="checkbox" checked={form.also_trigger_coi_workflow}
                            onChange={(e) => setField("also_trigger_coi_workflow", e.target.checked)}
                            data-testid="import-rental-also-coi" className="mt-0.5" />
                        <div>
                            <p className="font-medium text-slate-900">Also trigger a fresh COI verification workflow now</p>
                            <p className="text-xs text-slate-500">If you want an up-to-date COI from the agent in addition to the paper COI on file. The rental stays in its preselected status; the request is recorded in COI history.</p>
                        </div>
                    </label>

                    {/* Phase N.8 — collapsed "Optional: attach first invoice now"
                        toggle. Only relevant when the rental is being imported into
                        active status; scheduled/ended imports never need a first
                        invoice at import time (the release-time gate handles that). */}
                    {form.status_preselect === "active" && (
                        <div className="rounded-md border border-slate-200">
                            <button
                                type="button"
                                onClick={() => setShowInvoiceFields((v) => !v)}
                                className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-sm font-medium text-slate-800 hover:bg-slate-50"
                                data-testid="import-rental-optional-first-invoice-toggle"
                                aria-expanded={showInvoiceFields}
                            >
                                <span className="flex items-center gap-2">
                                    {showInvoiceFields ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                    Optional: attach first invoice now
                                </span>
                                <span className="text-[11px] font-normal text-slate-500">
                                    {showInvoiceFields ? "Fields shown" : "Skipped — add later"}
                                </span>
                            </button>
                            {showInvoiceFields && (
                                <div className="border-t border-slate-200 px-3 py-3 grid grid-cols-1 sm:grid-cols-2 gap-3 bg-slate-50/60">
                                    <Field
                                        label="First invoice amount (cents)"
                                        type="number"
                                        v={form.first_invoice_amount_cents}
                                        onChange={(v) => setField("first_invoice_amount_cents", v)}
                                        testId="import-rental-first-invoice-amount"
                                    />
                                    <Field
                                        label="Service period start"
                                        type="date"
                                        v={form.first_invoice_service_period_start}
                                        onChange={(v) => setField("first_invoice_service_period_start", v)}
                                        testId="import-rental-first-invoice-period-start"
                                    />
                                </div>
                            )}
                        </div>
                    )}

                    {/* Phase N.8 — collapsed "Advanced" showing the auto-filled
                        bypass reason for transparency. Only visible when the invoice
                        fields are collapsed (i.e. the bypass path will fire). */}
                    {form.status_preselect === "active" && !showInvoiceFields && (
                        <div className="rounded-md border border-slate-200">
                            <button
                                type="button"
                                onClick={() => setShowAdvanced((v) => !v)}
                                className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                                data-testid="import-rental-advanced-toggle"
                                aria-expanded={showAdvanced}
                            >
                                {showAdvanced ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                Advanced
                            </button>
                            {showAdvanced && (
                                <div className="border-t border-slate-200 px-3 py-3 space-y-2 bg-slate-50/60"
                                    data-testid="import-rental-advanced-reason">
                                    <Label className="text-xs text-slate-600">Bypass reason (recorded in audit trail)</Label>
                                    {reasonEditable ? (
                                        <Input
                                            value={form.skip_invoice_gate_reason || ""}
                                            onChange={(e) => setField("skip_invoice_gate_reason", e.target.value)}
                                            data-testid="import-rental-advanced-reason-input"
                                            placeholder={DEFAULT_BYPASS_REASON}
                                        />
                                    ) : (
                                        <p className="text-sm text-slate-800"
                                            data-testid="import-rental-advanced-reason-text">
                                            {form.skip_invoice_gate_reason || DEFAULT_BYPASS_REASON}
                                        </p>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => setReasonEditable((v) => !v)}
                                        className="text-xs font-medium text-blue-900 underline hover:no-underline"
                                        data-testid="import-rental-advanced-reason-edit"
                                    >
                                        {reasonEditable ? "Use default reason" : "Edit reason"}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={close} data-testid="import-rental-cancel">Cancel</Button>
                    <Button onClick={onSubmit} disabled={submitting} className="bg-blue-900 hover:bg-blue-800 gap-1.5" data-testid="import-rental-submit-btn">
                        <Upload size={14} /> {submitting ? "Importing…" : "Import rental"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function Field({ label, v, onChange, type = "text", testId, required }) {
    return (
        <div className="space-y-1.5">
            <Label>{label}{required && <span className="text-red-500 ml-1">*</span>}</Label>
            <Input type={type} value={v ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
        </div>
    );
}

function DropZone({ label, file, onChange, accept, testId, hint, required }) {
    return (
        <div className="space-y-1.5">
            <Label>{label}{required && <span className="text-red-500 ml-1">*</span>}</Label>
            <label
                htmlFor={testId}
                data-testid={`${testId}-zone`}
                className={`flex flex-col items-center justify-center gap-1.5 rounded-md border-2 border-dashed cursor-pointer transition-colors p-5 ${
                    file ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200 bg-slate-50/60 hover:border-slate-300"
                }`}
            >
                {file ? (
                    <>
                        <Paperclip size={18} className="text-emerald-700" />
                        <p className="text-xs font-medium text-emerald-900 truncate max-w-[18rem]">{file.name}</p>
                        <p className="text-[10px] text-slate-500">{(file.size / 1024).toFixed(0)} KB · click to replace</p>
                    </>
                ) : (
                    <>
                        <FilePlus size={18} className="text-slate-400" />
                        <p className="text-xs text-slate-600">Click to choose a file</p>
                    </>
                )}
                <input id={testId} type="file" accept={accept} className="hidden"
                    data-testid={testId}
                    onChange={(e) => onChange(e.target.files?.[0] || null)} />
            </label>
            {hint && <p className="text-[11px] text-slate-500">{hint}</p>}
        </div>
    );
}
